import { IDestroyOptions, Sprite, TextStyle, Text, Container, Rectangle } from "pixi.js";
import { BaseScalableView } from "../../../../common/BaseScalableView";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { SocialCommunitiesResourcePackage } from "../../resources/SocialCommunitiesResourcePackage";
import { GameFont } from "../../../../common/GameFont";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { gsap } from "gsap";
import { SocialItemsConfig } from "../configs/SocialItemsConfig";
import { SocialContainer } from "../components/SocialContainer";
import { DefaultButton } from "../../../../common/components/DefaultButton";

export class SocialCommunitiesPopupView extends BaseScalableView<void> {
    private static readonly JOIN_TEXT = "JOIN OUR";
    private static readonly SOCIAL_COMMUNITIES_TEXT = "SOCIAL COMMUNITIES";
    private static readonly CLICKING_ICONS_TEXT = "BY CLICKING ON\nICONS BELOW:";

    private readonly commonResources: CommonResourcesPackage;
    private readonly socialResourcePackage: SocialCommunitiesResourcePackage;

    private viewBackground: Sprite;
    private glassBg: Sprite;
    private joinText: Text;
    private socialText: Text;
    private clickingText: Text;
    private line: Sprite;
    private backButton: DefaultButton;
    private socialItemsContainer: Container;
    private socialItemsConfig: SocialItemsConfig;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        commonResources: CommonResourcesPackage,
        socialResourcePackage: SocialCommunitiesResourcePackage,
        canvasScaler: ICanvasScaler
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.socialResourcePackage = socialResourcePackage;
        this.socialItemsConfig = new SocialItemsConfig(socialResourcePackage);
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public async start(): Promise<void> {
        const showAnimation = new gsap.core.Timeline();
        showAnimation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.3, ease: "power1.in" });
        await showAnimation.play();
        showAnimation.kill();
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";
        this.addChild(this.viewBackground);
        this.glassBg = new Sprite(this.socialResourcePackage.assets_views_social_communities_glass_bg_png);
        this.addChild(this.glassBg);
        this.joinText = new Text(SocialCommunitiesPopupView.JOIN_TEXT, new DefaultTextTextStyle());
        this.addChild(this.joinText);
        this.socialText = new Text(SocialCommunitiesPopupView.SOCIAL_COMMUNITIES_TEXT, new SocialTextTextStyle());
        this.addChild(this.socialText);
        this.clickingText = new Text(SocialCommunitiesPopupView.CLICKING_ICONS_TEXT, new DefaultTextTextStyle());
        this.addChild(this.clickingText);
        this.line = new Sprite(this.socialResourcePackage.assets_views_social_communities_line_png);
        this.addChild(this.line);
        this.socialItemsContainer = new Container();
        this.addChild(this.socialItemsContainer);
        this.socialItemsConfig.itemsDescription.forEach(item => {
            const socialContainer = new SocialContainer(item.iconTexture, item.text, item.redirectUrl);
            socialContainer.position = item.position;
            this.socialItemsContainer.addChild(socialContainer);
        });
        this.backButton = new DefaultButton(this.commonResources.assets_common_ui_back_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.backButton.onUp.add(this.backButtonClickHandle, this);
        this.addChild(this.backButton);
    }

    private async backButtonClickHandle(): Promise<void> {
        this.backButton.eventMode = "none";
        await this.hideView();
        this.operationControls.complete();
        this.backButton.eventMode = "static";
    }

    private async hideView(): Promise<void> {
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
    }

    protected layout(width: number, _height: number): void {
        this.glassBg.position.set(84, 193);
        this.joinText.position.set(width * 0.5 - this.joinText.width * 0.5, 280);
        this.socialText.position.set(width * 0.5 - this.socialText.width * 0.5, 355);
        this.clickingText.position.set(width * 0.5 - this.clickingText.width * 0.5, 430);
        this.line.position.set(220, 633);
        this.socialItemsContainer.position.set(216, 700);
        this.backButton.position.set(140, 225);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.backButton.onUp.remove(this.backButtonClickHandle, this);
        super.destroy(options);
    }
}

class DefaultTextTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#FFFFFF",
            fontFamily: GameFont.DEFAULT_FONT,
            leading: 5,
            fontSize: 75
        }));
    }
}

class SocialTextTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: [
                "#a8fc09",
                "#21d214"
            ],
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowAngle: 0.9,
            dropShadowBlur: 5,
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 75
        }));
    }
}
